import React, {useEffect, useMemo, useState} from "react";
import Layout from "../../../common/Layout";
import SEO from "../../../common/SEO";
import FooterOne from "../../../common/footer/FooterOne";
import IntegrationsCarousel from "../../../components/banner/integrations";
import RealtimeSyncWidget from "../../../components/widgets/RealtimeSyncWidget";
import { useLocation } from "react-router-dom";
import Demo from "../../../innerpages/Demo";
import ContactModalButton from "../../../components/common/ContactModalButton";
import {useTranslation} from 'react-i18next';

const SectionHeader = ({ label, description = "", bg = false }) => (
  <div className={`edu-about-area ${bg ? 'about-style-3 bg-image' : ''}`} style={{ margin: "30px 0", padding: "4rem 0" }}>
    <div className="container eduvibe-animated-shape">
      <div className="row g-5 align-items-center">
        <div className="col-lg-12">
          <div className="section-title text-center">
            <h3 className="title">{label}</h3>
            {description && (
              <p
                  className="description mt--20"
                  style={{ maxWidth: 900, margin: "0 auto" }}
                  dangerouslySetInnerHTML={{ __html: description }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  </div>
);

const FullWidthSpacer = () => (
  <div aria-hidden="true" style={{ width: "100%", height: 60, background: "linear-gradient(90deg,#f8fbff 0%, #f3f6fb 50%, #f8fbff 100%)", margin: "30px 0" }} />
);

// Gastro-like small top spacer with background and white headline
const TopSpacer = ({ title = "", bgUrl }) => (
  <div className="edu-breadcrumb-area breadcrumb-style-1 ptb--35 ptb_md--40 ptb_sm--40 bg-image" style={{ backgroundImage: `url("${bgUrl}")` }}>
    <div className="container eduvibe-animated-shape">
      <div className="row">
        <div className="col-lg-12">
          <div className="breadcrumb-inner text-start">
            <div className="page-title">
              <h3 className="title"><span style={{ color: '#fff' }}>{title}</span></h3>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

// Gastro-like two-column module block with circular image and feature list
const CrmModule = ({ preTitle, title, desc, imageUrl, consultLabelKey, features = [] , t}) => (
  <div className="edu-about-area about-style-3 edu-section-gap bg-image">
    <div className="container eduvibe-animated-shape">
      <div className="row g-5">
        <div className="col-lg-6" id="delivery-module">
          <div className="inner">
            <div className="section-title text-start">
              {preTitle && (
                <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  <span className="pre-title">{preTitle}</span>
                </div>
              )}
              <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                <h3 className="title">{title}</h3>
              </div>
            </div>
            {desc && (
              <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                <p className="description mt--40">{desc}</p>
              </div>
            )}
            {imageUrl && (
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '5rem 0px' }}>
                <div className="thumbnail">
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                    <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                      <ContactModalButton>
                        {t(consultLabelKey)}
                      </ContactModalButton>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="col-lg-6" id="delivery-module">
          <div className="inner">
            {features.map((f, idx) => (
              <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                <div className="edu-feature-list">
                  <div className="content">
                    <h6 className="title d-flex align-items-center">
                      <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>{f.title}
                    </h6>
                    {f.description && <p className="description">{f.description}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="shape-dot-wrapper shape-wrapper d-xl-block d-none">
        <div className="shape-image shape-image-1"><img src="/images/shapes/shape-21.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-2"><img src="/images/shapes/shape-13-04.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-3"><img src="/images/shapes/shape-03-05.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-4"><img src="/images/shapes/shape-15-02.png" alt="Shape Thumb" /></div>
      </div>
    </div>
  </div>
);

// Alternating gastro-like row with circular image and animated circles
const AlternatingCrmRow = ({ title, desc, consultLabelKey, features = [], imageUrl, reverse = false, t }) => (
  <div className="edu-about-area about-style-3 edu-section-gap bg-image" style={{ paddingTop: '3rem', paddingBottom: '3rem' }}>
    <div className="container eduvibe-animated-shape">
      <div className="row g-5 align-items-center">
        {!reverse ? (
          <>
            <div className="col-lg-12 section-title text-start">
              <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                <h3 className="title">{title}</h3>
              </div>
              {desc && (
                  <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                    <p className="description mt--40">{desc}</p>
                  </div>
              )}
            </div>
            <div className="col-lg-6">
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '2rem 0' }}>
                <div className="thumbnail">
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                    <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                      <ContactModalButton>
                        {t(consultLabelKey)}
                      </ContactModalButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="inner">
                <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  {features.map((f, idx) => (
                      <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                        <div className="edu-feature-list">
                          <div className="content">
                            <h6 className="title d-flex align-items-center">
                              <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>{f.title}
                            </h6>
                            {f.description && <p className="description">{f.description}</p>}
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="col-lg-12 section-title text-start">
              <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                <h3 className="title">{title}</h3>
              </div>
              {desc && (
                  <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                    <p className="description mt--40">{desc}</p>
                  </div>
              )}
            </div>
            <div className="col-lg-6 order-lg-2">
              <div className="testimonial-left-image animated fadeIn" style={{ animationDuration: '1s', margin: '2rem 0' }}>
                <div className="thumbnail" style={{textAlign: 'right'}}>
                  <div className="circle-image-wrapper" style={{ width: '52rem' }}>
                    <img className="radius-round" src={imageUrl} alt={title} style={{ width: '50rem', height: '50rem', objectFit: 'cover' }} />
                    <div className="circle-image"><span></span><span></span></div>
                    <div className="btn-area" style={{display: "flex", gap: 16, flexWrap: "wrap", marginTop: 40, justifyContent: "center"}}>
                      <ContactModalButton>
                        {t(consultLabelKey)}
                      </ContactModalButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-lg-6 order-lg-1">
              <div className="inner">
                <div className="animated fadeInUp" style={{ animationDuration: '1s' }}>
                  {features.map((f, idx) => (
                      <div key={`gmf-${idx}`} className="col-lg-12 p-0 animated fadeInUp" style={{ animationDuration: '1s' }}>
                        <div className="edu-feature-list">
                          <div className="content">
                            <h6 className="title d-flex align-items-center">
                              <i className="fas fa-check-square" style={{color: "#f78c45", marginRight: 6}}></i>{f.title}
                            </h6>
                            {f.description && <p className="description">{f.description}</p>}
                          </div>
                        </div>
                      </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        )}
      </div>
      <div className="shape-dot-wrapper shape-wrapper d-xl-block d-none">
        <div className="shape-image shape-image-1"><img src="/images/shapes/shape-21.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-2"><img src="/images/shapes/shape-13-04.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-3"><img src="/images/shapes/shape-03-05.png" alt="Shape Thumb" /></div>
        <div className="shape-image shape-image-4"><img src="/images/shapes/shape-15-02.png" alt="Shape Thumb" /></div>
      </div>
    </div>
  </div>
);

const CrmPageContent = () => {
  const { t } = useTranslation(); // Destructure t here
  // Icon features inspired by the reference page
  const coreBenefits = useMemo(() => ([
    { icon: "bx bx-globe", title: "Többnyelvű felület", text: "Nemzetközi értékesítés támogatása – ugyanaz a rendszer több nyelven." },
    { icon: "bx bx-grid-alt", title: "Moduláris felépítés", text: "Csak azokért a modulokért fizetsz, amikre szükséged van." },
    { icon: "bx bx-cloud", title: "Felhő és on-premise", text: "Rugalmas telepítés és biztonságos hozzáférés bárhonnan." },
    { icon: "bx bx-mail-send", title: "Email & kampányok", text: "Beépített hírlevél, kampány, értesítések – automatizáltan." },
    { icon: "bx bx-trending-up", title: "Riportok & BI", text: "Valós idejű kimutatások, KPI-ok és vezetői dashboard." },
    { icon: "bx bx-support", title: "Magyar támogatás", text: "Folyamatos frissítés, biztonsági mentés és hazai support." },
  ]), []);

  const keyFeatures = useMemo(() => ([
    { icon: "bx bx-cog", title: "Iparágfüggetlen, mégis testreszabható", text: "Rugalmas felépítés, ami a te folyamataidhoz igazodik. 🧩" },
    { icon: "bx bx-calendar", title: "Integrált naptár és meetingkezelés", text: "Google + Apple szinkron, meghívók és emlékeztetők. 📆" },
    { icon: "bx bx-envelope", title: "Sablon e-mailek", text: "Profi kommunikáció pár kattintással. ✉️" },
    { icon: "bx bx-cloud", title: "Felhőalapú, bárhonnan elérhető", text: "Biztonságos hozzáférés bárhonnan, bármikor. ☁️" },
    { icon: "bx bx-extension", title: "Bővíthető modulokkal", text: "Marketing és Projekt modul percek alatt bekapcsolható. ➕" },
    { icon: "bx bx-support", title: "Magyar nyelvű felület és támogatás", text: "Hazai ügyfélszolgálat és dokumentáció. 🇭🇺" },
    { icon: "bx bx-badge-check", title: "Nincs hűségidő, nincs rejtett díj", text: "Kiszámítható, átlátható költségek. ✅" },
  ]), []);

  const industrySolutions = useMemo(() => ([
    { icon: "bx bx-store", title: "Kereskedelem & Nagyker", points: [
      "Készlet- és raktárkezelés (több raktár)",
      "POS és pénztárgép integráció",
      "NAV, Számlázz.hu, Billingo",
      "Franchise és több bolt / üzlet támogatás",
    ]},
    { icon: "bx bx-restaurant", title: "Vendéglátás & Szálloda", points: [
      "Asztalfoglalás, digitális étlap, mobil pincér",
      "Szobafoglalás, recepciós modul",
      "Futárelszámolás (Wolt, Foodora)",
      "NTAK megfelelés",
    ]},
    { icon: "bx bx-spa", title: "Szolgáltató ipar / Szépség", points: [
      "Online időpontfoglalás és emlékeztetők",
      "Digitális naptár és automatikus szinkron",
      "Ügyfélkártya és előzmények",
      "Jutalékok és pénzügyi riportok",
    ]},
    { icon: "bx bx-cart", title: "Webshop & E-kereskedelem", points: [
      "Shoprenter, UNAS, Shopify integráció",
      "Automatikus ár- és készletfrissítés",
      "NAV-jelentések, utánvét kezelés",
      "Bankkártyás fizetés és könyvelés",
    ]},
    { icon: "bx bx-hard-hat", title: "Projekt & Építőipar", points: [
      "Projekt- és feladatkezelés",
      "Költségkontroll és alvállalkozói elszámolás",
      "Anyagfelhasználás, raktárkövetés",
      "Dokumentum- és engedélykezelés",
    ]},
    { icon: "bx bx-wrench", title: "Szerviz & Karbantartás", points: [
      "Online ütemezés és időpontfoglalás",
      "Szervizlapok és munkalapok",
      "Alkatrész- és készletkezelés",
      "Email/SMS értesítések",
    ]},
  ]), []);

  const integrations = useMemo(() => ([
    "Számlázz.hu", "Billingo", "NAV Online Számla", "Barion", "OTP Simple", "Stripe", "Google", "Mailchimp", "Zapier", "Slack"
  ]), []);

  const steps = useMemo(() => ([
    { icon: "bx bx-chat", title: "1. Felmérés", text: "Igények egyeztetése, folyamatok feltérképezése." },
    { icon: "bx bx-cog", title: "2. Beállítás", text: "Modulok kiválasztása és rendszerek összekötése." },
    { icon: "bx bx-transfer", title: "3. Adatmigráció", text: "Termékek, partnerek, történetek áthozása." },
    { icon: "bx bx-book-open", title: "4. Oktatás", text: "Kulcsfelhasználók betanítása és dokumentáció." },
    { icon: "bx bx-rocket", title: "5. Éles indulás", text: "Támogatott indulás és SLA alapú support." },
  ]), []);

  const [sat, setSat] = useState(0);
  const [faster, setFaster] = useState(0);
  const [retention, setRetention] = useState(0);

  // Active section highlight for sticky sub-navigation
  const crmSectionIds = useMemo(() => ([
    "crm-hero",
    "crm-benefits",
    "crm-erp",
    "crm-stats",
    "crm-industries",
    "crm-integrations",
    "crm-steps",
    "crm-cta",
  ]), []);
  const [activeId, setActiveId] = useState("crm-hero");
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { root: null, rootMargin: "-30% 0px -60% 0px", threshold: 0.01 }
    );
    const elements = crmSectionIds.map((id) => document.getElementById(id)).filter(Boolean);
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [crmSectionIds]);

  useEffect(() => {
    const animate = (setter, target, duration = 1200) => {
      const start = performance.now();
      const step = (t) => {
        const p = Math.min((t - start) / duration, 1);
        const val = Math.round(target * p);
        setter(val);
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };

    animate(setSat, 98);
    animate(setFaster, 35);
    animate(setRetention, 20);
  }, []);

  // const { pathname } = useLocation();
  // const host = (window.location.hostname || "").replace(/^www\./i, "").toLowerCase();
  //
  // const showHeaderImage = host === "happytrade.hu" && pathname === "/";

  return (
    <>
      <SEO title="Happy CRM - Ügyfélkapcsolatok új szinten" description="Ügyfélkapcsolatok új szinten – átláthatóan, automatizáltan. Építsd a lojalitást és kezeld ügyfeleidet egy helyen." url={typeof window !== 'undefined' ? window.location.href : undefined} canonical={typeof window !== 'undefined' ? window.location.href : undefined} />
      <Layout>
        {/*{showHeaderImage && (*/}
        {/*    <div className="col-lg-12">*/}
        {/*      <div className="inner">*/}
        {/*        <img src={"https://images.happysolutions.hu/Happy_Trade_Header_1920x1080.png"} alt="Happy Trade"/>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*)}*/}

        {/*<div className="col-lg-12" style={{ marginBottom: "2rem" }}>*/}
        {/*  <div className="col-lg-12">*/}
        {/*    <div className="inner">*/}
        {/*      <div className="col-lg-12 content text-start">*/}
        {/*        <h1 className="title">Happy CRM</h1>*/}
        {/*        <p className="description" style={{ fontSize: 18 }}>*/}
        {/*          Sikeres ügyfélkapcsolatok, garantált növekedés*/}
        {/*        </p>*/}
        {/*        <div className="btn-area" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>*/}
        {/*          <button className="edu-btn" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Kérem a demót">*/}
        {/*            Kérem a demót <i className="icon-arrow-right-line-right"></i>*/}
        {/*          </button>*/}
        {/*          <button className="edu-btn btn-secondary" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Konzultációt kérek">*/}
        {/*            Ingyenes konzultáció*/}
        {/*          </button>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Hero */}
        <div id="crm-hero" className="slider-area banner-style-3 bg-image" style={{ margin: "30px 0", padding: "2rem 0", background: 'url(https://images.happysolutions.hu/CRM-oldal-hero-1920-500.png)', backgroundSize: 'cover', backgroundPosition: 'center center', minHeight: 400 }}>
          <div className="d-flex align-items-center" style={{paddingBottom: 40}}>
            <div className="container eduvibe-animated-shape">
              <div className="row g-5 align-items-center">
                <div className="col-lg-12">
                  <div className="inner">
                    <div className="content text-start" style={{color: 'white'}}>
                      <h1 className="title" style={{color: 'white'}}>HAPPY CRM</h1>
                      <p className="description" style={{ fontSize: 18, color: 'white' }}>
                        Sikeres ügyfélkapcsolatok, garantált növekedés
                      </p>											
                      <div className="btn-area" style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
												<Demo title={'Kérem a demót'}/>
                        {/* <button className="edu-btn" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Kérem a demót">
                          Kérem a demót <i className="icon-arrow-right-line-right"></i>
                        </button> */}
                        <button className="edu-btn btn-secondary" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Konzultációt kérek">
                          Ingyenes konzultáció
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <SectionHeader
            label="Egy CRM, ami nem korlátoz, hanem építkezik."
            description="A Happy CRM egy <b>iparág független ügyfélkezelő rendszer</b>, amely egy helyen kezeli az ügyféladatokat, az értékesítési folyamatokat és a kapcsolódó kommunikációt.
            <b>Az ügyfél, az értékesítő és a vezető valós időben ugyanazt az információt látja</b>, így az együttműködés átláthatóbbá és hatékonyabbá válik. A Happy CRM egyszerűen használható, rugalmasan bővíthető, és stabil alapot ad az értékesítés, a marketing és a projektalapú működéshez."
        />

        {/*<TopSpacer*/}
        {/*    title="Happy CRM modulok"*/}
        {/*    bgUrl="https://images.unsplash.com/photo-1563986768711-b3bde3dc8217?q=80&w=3270&auto=format&fit=crop&ixlib=rb-4.0.3"*/}
        {/*/>*/}
        
        <CrmModule
          title="Happy CRM modul"
          desc="Minden ügyféladat egy helyen – automatizált kommunikációval és mérhető eredményekkel."
          imageUrl="https://images.happysolutions.hu/CRM-fekvo%CC%8B-1920-1080.png"
          consultLabelKey={'tradePage.hero.ctaConsult'}
          features={[
            { title: "Ügyféladatbázis", description: "Minden ügyféladat egy központi rendszerben érhető el: kapcsolattartók, kommunikációs előzmények, ajánlatok, vásárlások és aktivitások egyetlen ügyfélkártyán. A szegmensek és címkék segítségével az ügyfelek könnyen csoportosíthatók, így a sales és marketing célzottan, adatvezérelt módon dolgozhat." },
            { title: "Kampányok & automatizmusok", description: "Email és SMS kampányok indítása, időzített üzenet sorozatok és esemény-alapú triggerek (pl. ajánlatkérés, státuszváltás, kosárelhagyás) támogatják a folyamatos és személyre szabott ügyfél kommunikációt. Az automatizmusok csökkentik a manuális munkát és növelik a konverziót." },
            { title: "Integrációk", description: "ERP, számlázó, webshop és analitikai rendszerek összekapcsolása biztosítja, hogy minden üzleti adat egy platformon, valós időben elérhető legyen. Az automatikus adatátadás megszünteti az adatszigeteket és egységes működést teremt." },
            { title: "Dashboard & KPI", description: "Valós idejű kampányeredmények, értékesítési pipeline, konverziós arányok és csapatteljesítmény mutatók segítik a gyors és megalapozott döntéshozatalt. A testreszabható KPI-ok átláthatóvá teszik a működést és támogatják a folyamatos optimalizálást.\n" }
          ]}
          t={t}
        />

        <SectionHeader label="A Happy CRM rendszer alapfunkciói:" />
          <div className="container text-center">
            <p>A Happy CRM a <b>napi ügyfél kommunikáció</b> és <b>szervezés központi platformja</b>: egy helyen kezeli a <b>naptárakat, meetingeket, e-maileket és sablonokat</b>.</p>
            <p>Segít abban, hogy minden egyeztetés, üzenet és aktivitás átlátható, mérhető és visszakereshető legyen – így a csapat szervezettebben, gyorsabban és profibban működik.</p>
          </div>

        <AlternatingCrmRow
          title="Ügyfél- és kapcsolatkezelés"
          desc="A rendszer átfogó megoldást biztosít az ügyfélkapcsolatok teljes életciklusának kezelésére az első kapcsolatfelvételtől a hosszú távú együttműködés. Segíti a strukturált, átlátható és tudatos ügyfélmenedzsmentet."
          consultLabelKey={'tradePage.hero.ctaConsult'}
          imageUrl="https://images.happysolutions.hu/csaj-helyett-crm-hez.jpg"
          reverse
          features={[
            { title: "Teljes ügyfél- és partner adatbázis", description: "Minden ügyfél- és partner információ egy központi rendszerben érhető el."},
            { title: "Kapcsolattartók, címek, státuszok és aktivitásnapló", description: "Részletes ügyfélkártyák tartalmazzák a kapcsolattartók adatait, státuszát és az összes kommunikációs előzményt. Így minden interakció nyomon követhető és visszakereshető."},
            { title: "Feladatok, határidők, automatikus értesítések", description: "Kiosztható feladatok és határidők biztosítják, hogy egyetlen fontos lépés se maradjon el. Az automatikus értesítések támogatják a proaktív ügyfélkezelést."},
            { title: "Értékesítési pipeline – ajánlat → tárgyalás → lezárás", description: "Átlátható folyamatban követhető az értékesítés minden szakasza az első ajánlattól a sikeres lezárásig. Segít a bevételi előrejelzésben és a konverzió javításában."},
            { title: "Személyre szabható státuszok és ügyféltípusok", description: "Az egyedi státuszok és kategóriák a cég saját működéséhez igazíthatók. Így a CRM valóban a vállalkozás folyamatait támogatja, nem fordítva."}
          ]}
          t={t}
        />
        
        <AlternatingCrmRow
          title="Kommunikáció és naptár"
          desc="A CRM központi felületen kezeli az ügyfél egyeztetéseket, belső meetingeket és határidőket, így minden időpont és kapcsolódó kommunikáció átláthatóan követhető. Segíti a csapat összehangolt működését és csökkenti az adminisztrációt."
          consultLabelKey={'tradePage.hero.ctaConsult'}
          imageUrl="https://images.happysolutions.hu/pe%CC%81nzu%CC%88gyi-modul.jpg"
          features={[
            { title: "Google Naptár és Apple Calendar szinkron", description: "Kétirányú szinkronizáció biztosítja, hogy a CRM-ben rögzített események automatikusan megjelenjenek a külső naptárakban, és fordítva. Így elkerülhetők az ütközések és minden időpont naprakész marad."},
            { title: "Meeting-szervezés Google Meet integrációval", description: "A rendszer automatikusan létrehozza a meeting linket, kiküldi a meghívót és kezeli az emlékeztetőket. Az online megbeszélések szervezése gyorsabbá és professzionálisabbá válik."},
            { title: "Esemény-emlékeztető e-mailben vagy mobil push értesítéssel", description: "A közelgő találkozókról és határidőkről a rendszer automatikus értesítést küld, így csökken az elmaradt egyeztetések száma és nő a pontosság."},
            { title: "Napi, heti és havi nézet – csapat időpontok áttekintése", description: "Rugalmas naptár nézetek segítik az egyéni és csapat szintű tervezést. A vezetők és munkatársak egyaránt könnyen átlátják a kapacitásokat és a terhelést."}
          ]}
          t={t}
        />
        
        <AlternatingCrmRow
          title="E-mailek és sablonok"
          consultLabelKey={'tradePage.hero.ctaConsult'}
          desc="A CRM integrált e-mail kezelő rendszere biztosítja, hogy minden ügyfélkommunikáció egy helyen, strukturáltan és visszakereshetően legyen tárolva, támogatva az egységes és professzionális megjelenést."
          imageUrl="https://images.happysolutions.hu/crm-emailek-melle-kep.jpg"
          reverse
          features={[
            { title: "Sablon e-mailek: ajánlatküldés, visszaigazolás, follow-up", description: "Előre elkészített, testre szabható sablonok gyorsítják az adminisztrációt és biztosítják az egységes kommunikációt az értékesítési és ügyfélkezelési folyamat minden szakaszában."},
            { title: "Automatikus személyes megszólítások és aláírások", description: "A rendszer az ügyféladatok alapján automatikusan személyre szabja az üzenetek megszólítását és aláírását, így a kommunikáció egyszerre hatékony és személyes marad."},
            { title: "Egy kattintásos küldés a CRM-ből", description: "Az e-mailek közvetlenül a CRM felületéről küldhetők, külső levelezőprogram használata nélkül, ami gyorsabb munkavégzést és teljes körű dokumentáltságot biztosít."},
            { title: "Küldési és megnyitási statisztika", description: "A rendszer méri a kiküldések, megnyitások és kattintások adatait, így pontos visszajelzést ad a kommunikáció hatékonyságáról."},
            { title: "Kommunikációs központ", description: "A CRM nem csupán levelezőfelület, hanem teljes körű kommunikációs központ, ahol minden ügyfél interakció nyomon követhető, visszakereshető és mérhető, biztosítva az átlátható és tudatos ügyfélkezelést."}
          ]}
          t={t}
        />

        <SectionHeader label="A Happy CRM rendszer tovább bővíthető az alábbi modulokkal" />
        <div className="container text-center">
          <p>A Happy CRM rendszer modulárisan bővíthető marketing-, projekt-, riport- és automatizációs modulokkal, így a vállalkozás igényeihez igazodva fokozatosan alakítható komplex üzleti platformmá.</p>
        </div>

        <AlternatingCrmRow
          title="Marketing modul"
          desc="A marketing modul támogatja a célzott ügyfélkommunikációt és a leadek rendszerbe szervezett kezelését, segítve az érdeklődők ügyféllé alakítását és a visszatérő vásárlások ösztönzését."
          consultLabelKey={'tradePage.hero.ctaConsult'}
          imageUrl="https://images.happysolutions.hu/marketing-modul-ke%CC%81p.jpg"
          features={[
            { title: "Célzott e-mail kampányok és szegmensek", description: "Az ügyfél adatbázis alapján pontosan szegmentált listák hozhatók létre, így a kampányok a megfelelő célcsoporthoz jutnak el a megfelelő üzenettel. Ez növeli a megnyitási arányt, a konverziót és a kampányok hatékonyságát."},
            { title: "Hírlevél-sablonok, aktivitás-riport", description: "Testre szabható hírlevél-sablonok biztosítják az egységes arculatot és a gyors kampányindítást. Az aktivitás-riportok részletes képet adnak a megnyitásokról, kattintásokról és reakciókról."},
            { title: "Lead import weboldalról vagy webshopból", description: "A rendszer automatikusan fogadja és rögzíti a weboldalról vagy webshopból érkező érdeklődőket, így minden új lead azonnal bekerül az értékesítési folyamatba. Ez gyorsabb utánkövetést és hatékonyabb ügyfélszerzést tesz lehetővé."},
            { title: "Akció- és pontgyűjtő programok", description: "A modul lehetőséget ad promóciók, kedvezmények és hűségprogramok kezelésére. A pontgyűjtő rendszerek ösztönzik a visszatérő vásárlást és erősítik az ügyfélmegtartást."},
          ]}
          t={t}
        />
        
        <AlternatingCrmRow
          title="Projekt modul"
          desc="A projekt modul strukturált keretet biztosít a futó munkák átlátható kezeléséhez, összekapcsolva az ügyféladatokat a konkrét feladatokkal és határidőkkel. Segíti a csapatmunkát, a felelősségek tisztázását és a projektek határidőre történő megvalósítását."
          consultLabelKey={'tradePage.hero.ctaConsult'}
          imageUrl="https://images.happysolutions.hu/project-management-melletti-ke%CC%81p.jpg"
          reverse
          features={[
            { title: "Projektek, mérföldkövek és feladatcsoportok", description: "A projektek jól elkülönített szakaszokra és mérföldkövekre bonthatók, amelyekhez feladatcsoportok rendelhetők. Ez átláthatóvá teszi az előrehaladást és segít a prioritások meghatározásában."},
            { title: "CRM-ügyfelek összekapcsolása projektjeiddel", description: "Minden projekt közvetlenül összekapcsolható a CRM-ben szereplő ügyféllel, így az ajánlatok, egyeztetések és kapcsolódó dokumentumok egy helyen érhetők el. Ez biztosítja a teljes körű ügyfél- és projekt áttekintést."},
            { title: "Feladat felelősök, státuszok, naptár-integráció", description: "A feladatokhoz felelősök, határidők és státuszok rendelhetők, amelyek valós időben követhetők. A naptár-integráció segít a kapacitások tervezésében és a csapat összehangolt működésében."},
          ]}
          t={t}
        />

        <AlternatingCrmRow
          title="Riport modul"
          consultLabelKey={'tradePage.hero.ctaConsult'}
          desc="A riport modul valós idejű áttekintést ad az értékesítési és ügyfélkezelési folyamatok eredményességéről, támogatva az adatvezérelt döntéshozatalt."
          imageUrl="https://images.happysolutions.hu/riporting-modul-melle%CC%81.jpg"
          features={[
            { title: "Értékesítési és aktivitási statisztikák", description: "Részletes kimutatások készülnek a pipeline állapotáról, lezárásokról, ügyfélaktivitásokról és kampány eredményekről, így pontosan mérhető a csapat teljesítménye."},
            { title: "Teljesítmény- és határidő elemzés", description: "A rendszer elemzi a feladatok és projektek határidő-teljesítését, valamint az egyéni és csapatszintű hatékonyságot, segítve a folyamatok optimalizálását."},
            { title: "Egyéni jelentések exportálható formában", description: "Testre szabható riportok készíthetők és exportálhatók különböző formátumokban, így egyszerűen megoszthatók vezetői vagy pénzügyi döntéstámogatás céljából."},
          ]}
          t={t}
        />

        <AlternatingCrmRow
          title="Automatizáció modul"
          desc="Az automatizáció modul tehermentesíti a csapatot az ismétlődő adminisztratív feladatoktól, és biztosítja, hogy minden fontos ügyfél- vagy értékesítési eseményre időben reagáljatok."
          consultLabelKey={'tradePage.hero.ctaConsult'}
          imageUrl="https://images.happysolutions.hu/automatizmusok.jpg"
          reverse
          features={[
            { title: "Esemény-alapú értesítések", description: "A rendszer automatikus értesítéseket küld meghatározott események esetén – például új ügyfél rögzítésekor, elmaradt meetingnél vagy státuszváltozáskor – így semmilyen fontos lépés nem marad el."},
            { title: "Egyszerű workflow-k – időpontfoglalás után automatikus e-mail", description: "Előre beállított folyamatok indíthatók egy-egy esemény hatására, például időpontfoglalás után automatikus visszaigazoló vagy tájékoztató e-mail küldése. Ez gyorsabb, egységesebb és professzionálisabb ügyfélélményt biztosít."}
          ]}
          t={t}
        />

        {/* ERP integration as alternating row */}
        {/*<SectionHeader label="Integráció a Happy ERP rendszerrel? Igen!" />*/}
        {/*<div id="crm-erp" className="edu-about-area bg-image" style={{ padding: "3rem 0", margin: "30px 0" }}>*/}
        {/*  <div className="container">*/}
        {/*    <div className="alt-row">*/}
        {/*      <div className="alt-image">*/}
        {/*        <img style={{ borderRadius: 8 }} className="img-01" src="https://images.unsplash.com/photo-1556157382-97eda2d62296?q=80&w=1600&auto=format&fit=crop" alt="CRM és ERP összekapcsolás illusztráció" width="640" height="480" decoding="async" loading="lazy" />*/}
        {/*      </div>*/}
        {/*      <div className="alt-text">*/}
        {/*        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>*/}
        {/*          <h4 className="title" style={{ margin: 0 }}>Integráció a Happy ERP rendszerrel</h4>*/}
        {/*        </div>*/}
        {/*        <p className="description mt--10">A CRM teljes mértékben integrálható a Happy ERP moduljaival, így az értékesítés, számlázás és ügyfélszolgálat adatai valós időben, egységes rendszerben kezelhetők.</p>*/}
        {/*        <ul className="mt--10">*/}
        {/*          <li>✔ Közös ügyfél- és partner adatbázis jön létre</li>*/}
        {/*          <li>✔ Ajánlat ➝ rendelés ➝ számla folyamat egyben</li>*/}
        {/*          <li>✔ Email és kampányadatok az ERP riportokban is megjelennek</li>*/}
        {/*          <li>✔ Nyitott feladatok és ticketek megjelenítése</li>*/}
        {/*        </ul>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<div className="container" style={{ maxWidth: 980, textAlign: 'center', marginTop: '-1rem' }}>*/}
        {/*  <p className="description">Minden funkció egy célért dolgozik: hogy az ügyfél, az értékesítő és a vezető ugyanazt lássa — valós időben. ⏱️</p>*/}
        {/*</div>*/}

        {/*<FullWidthSpacer />*/}

        {/* Benefits in card layout like Trade page */}
        {/*<SectionHeader label="Miért a Happy CRM?" />*/}
        {/*<div id="crm-benefits" className="edu-about-area bg-color-white" style={{ margin: "30px 0", padding: "2rem 0 3rem" }}>*/}
        {/*  <div className="container">*/}
        {/*    <div className="row g-5">*/}
        {/*      {keyFeatures.map((b, i) => (*/}
        {/*        <div key={`benefit-card-${i}`} className="col-lg-3 col-md-6">*/}
        {/*          <div className="service-card service-card-3">*/}
        {/*            <div className="content">*/}
        {/*              <h5 className="title">{b.title}</h5>*/}
        {/*              <p className="description mt--10">{b.text}</p>*/}
        {/*            </div>*/}
        {/*          </div>*/}
        {/*        </div>*/}
        {/*      ))}*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<FullWidthSpacer />*/}

        {/* Customer metrics */}
        {/*<SectionHeader label="Ügyfélélmény számokban" />*/}
        {/*<div id="crm-stats" className="eduvibe-cta-area cta-style-7 bg-color-white" style={{ padding: "2.5rem 0", margin: "30px 0" }} aria-label="Ügyfélélmény számokban">*/}
        {/*  <div className="container">*/}
        {/*    <div className="row g-5 align-items-center">*/}
        {/*      <div className="col-lg-3"><h4 className="title" style={{ margin: 0 }}>Ügyfélélmény számokban</h4></div>*/}
        {/*      <div className="col-lg-9">*/}
        {/*        <div style={{ display: "flex", flexWrap: "wrap", gap: 24 }}>*/}
        {/*          <div className="service-card service-card-3" style={{ minWidth: 180, padding: 16 }}>*/}
        {/*            <div className="content" style={{ textAlign: "center" }}>*/}
        {/*              <div aria-label="Elégedettség" style={{ fontSize: 36, fontWeight: 700, color: "#1b4ae8" }}>{sat}%</div>*/}
        {/*              <div style={{ fontSize: 14 }}>Ügyfél-elégedettség</div>*/}
        {/*            </div>*/}
        {/*          </div>*/}
        {/*          <div className="service-card service-card-3" style={{ minWidth: 180, padding: 16 }}>*/}
        {/*            <div className="content" style={{ textAlign: "center" }}>*/}
        {/*              <div aria-label="Gyorsabb válaszidő" style={{ fontSize: 36, fontWeight: 700, color: "#1b4ae8" }}>{faster}%</div>*/}
        {/*              <div style={{ fontSize: 14 }}>Gyorsabb válaszidő</div>*/}
        {/*            </div>*/}
        {/*          </div>*/}
        {/*          <div className="service-card service-card-3" style={{ minWidth: 180, padding: 16 }}>*/}
        {/*            <div className="content" style={{ textAlign: "center" }}>*/}
        {/*              <div aria-label="Megtartás növekedése" style={{ fontSize: 36, fontWeight: 700, color: "#1b4ae8" }}>+{retention}%</div>*/}
        {/*              <div style={{ fontSize: 14 }}>Vevőmegtartás javulása</div>*/}
        {/*            </div>*/}
        {/*          </div>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* POS-like info + animated widget */}
        {/*<div className="happy-pos edu-event-details-area edu-event-details edu-section-gap bg-color-white" style={{ overflowX: 'hidden' }}>*/}
        {/*  <div className="container">*/}
        {/*    <div className="row g-5">*/}
        {/*      <div className="col-lg-7">*/}
        {/*        <div className="content" style={{ marginTop: 0 }}>*/}
        {/*          <h4 className="title" style={{ fontSize: 30 }}>még sok más, amire képesek vagyunk</h4>*/}
        {/*          <ul>*/}
        {/*            <li style={{ fontSize: 17 }}>Valós idejű forgalmi adatok bármilyen eszközről, bárhonnan*</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Készletkezelés valós idejű alapanyag fogyással a felütés pillanatában</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Szerkeszthető értesítések minden lényeges dologról, legyen az egy esemény (pl. ültetés, törlés, számla storno), forgalmi vagy készlet információ változásról</li>*/}
        {/*            <li style={{ fontSize: 17 }}><a className="text-primary" href="/termekeink/happy-pos" target="_blank" rel="noreferrer">Mobil rendelésfelvétel</a>, többféle érintésmentes fizetéssel</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Pénztárgép és adóügyi nyomtató integráció</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Megfelel az <a className="text-primary" href="https://ntak.hu/" target="_blank" rel="noreferrer">NTAK adatszolgáltatási</a> kötelezettségnek</li>*/}
        {/*          </ul>*/}
        {/*        </div>*/}
        {/*        <div className="content" style={{ marginTop: 0 }}>*/}
        {/*          <h4 className="title" style={{ fontSize: 30 }}>és még mindig nincs vége</h4>*/}
        {/*          <ul>*/}
        {/*            <li style={{ fontSize: 17 }}>Csoportos elrendezés a jobb átláthatóságért</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Inteligens termék kereső</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Átlátható felütött tételek lista, gyors tételszám és kedvezmény/extra módosítás</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Kedvezmény, törzsvendég gyors hozzáadása</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Elvitel-helyben fogyasztás gyorskapcsoló a változó áfa tartalom miatt</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Fogások, körök beállítása a konyha számára, következő fogás kérése</li>*/}
        {/*            <li style={{ fontSize: 17 }}>Átültetés asztalok között, számla bontási lehetőség</li>*/}
        {/*          </ul>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*      <div className="col-lg-5">*/}
        {/*        <RealtimeSyncWidget />*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/* Industry solutions as alternating rows */}
        {/*<SectionHeader label="Megoldások iparágak szerint" />*/}
        {/*<div id="crm-industries" className="edu-about-area bg-image" style={{ padding: "3rem 0", margin: "30px 0" }}>*/}
        {/*  <div className="container">*/}
        {/*    {industrySolutions.map((s, i) => (*/}
        {/*      <div key={`industry-row-${i}`} className={`alt-row ${i % 2 === 1 ? 'reverse' : ''}`} style={{ marginBottom: 24 }}>*/}
        {/*        <div className="alt-image">*/}
        {/*          <img src={`https://picsum.photos/seed/crm-industry-${i}/640/420`} alt={`${s.title} illusztráció`} width="640" height="420" loading="lazy" />*/}
        {/*        </div>*/}
        {/*        <div className="alt-text">*/}
        {/*          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>*/}
        {/*            <i className={`${s.icon}`} style={{ fontSize: 26, color: "#F2B34C" }}></i>*/}
        {/*            <h4 className="title" style={{ margin: 0 }}>{s.title}</h4>*/}
        {/*          </div>*/}
        {/*          <ul className="mt--10">*/}
        {/*            {s.points.map((p, k) => (<li key={`sp-${i}-${k}`}>✔ {p}</li>))}*/}
        {/*          </ul>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<FullWidthSpacer />*/}
        {/* Integrations strip (infinite carousel) */}
        {/*<SectionHeader label="Integrációk" />*/}
        {/*<div id="integrations" className="eduvibe-cta-area cta-style-7 bg-color-white" style={{ padding: "2.5rem 0", margin: "30px 0" }}>*/}
        {/*  <div className="container">*/}
        {/*    /!* Reusing global Integrations carousel component *!/*/}
        {/*    <IntegrationsCarousel />*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<FullWidthSpacer />*/}
        {/* How it works as alternating rows */}
        {/*<SectionHeader label="Bevezetés lépései" />*/}
        {/*<div id="crm-steps" className="edu-about-area bg-image" style={{ padding: "3rem 0", margin: "30px 0" }}>*/}
        {/*  <div className="container">*/}
        {/*    {steps.map((st, i) => (*/}
        {/*      <div key={`step-row-${i}`} className={`alt-row ${i % 2 === 1 ? 'reverse' : ''}`} style={{ marginBottom: 24 }}>*/}
        {/*        <div className="alt-image">*/}
        {/*          <img src={`https://picsum.photos/seed/crm-step-${i}/640/420`} alt={`${st.title} illusztráció`} width="640" height="420" loading="lazy" />*/}
        {/*        </div>*/}
        {/*        <div className="alt-text">*/}
        {/*          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>*/}
        {/*            <i className={`${st.icon}`} style={{ fontSize: 26, color: "#22c55e" }}></i>*/}
        {/*            <h4 className="title" style={{ margin: 0 }}>{st.title}</h4>*/}
        {/*          </div>*/}
        {/*          <p className="description mt--10">{st.text}</p>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    ))}*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<FullWidthSpacer />*/}
        {/* CTA banner */}
        {/*<SectionHeader label="Kapcsolat" />*/}
        {/*<div id="ask-demo" className="eduvibe-cta-area cta-style-7 bg-image" style={{ padding: "3rem 0 4rem", margin: "30px 0" }}>*/}
        {/*  <div className="container">*/}
        {/*    <div className="row g-5 align-items-center">*/}
        {/*      <div className="col-lg-8">*/}
        {/*        <h3 className="title">Happy CRM – a hatékony ügyfélkezelés új szintje.</h3>*/}
        {/*        <p className="description mt--10">Kérj személyre szabott demót vagy ingyenes konzultációt szakértőinktől.</p>*/}
        {/*      </div>*/}
        {/*      <div className="col-lg-4" style={{ display: "flex", gap: 12, justifyContent: "flex-end" }}>*/}
        {/*        <button className="edu-btn btn-secondary" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Konzultációt kérek">Konzultációt kérek</button>*/}
        {/*        /!* <button className="edu-btn" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }} aria-label="Kérem a demót">Kérem a demót</button> *!/*/}
		{/*						*/}
		{/*						<Demo title={'Kérem a demót'}/>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        {/*<div id="contact-us" className="edu-contact-area" style={{ padding: "2rem 0 4rem" }}>*/}
        {/*  <div className="container">*/}
        {/*    <div className="row g-5">*/}
        {/*      <div className="col-lg-8 offset-lg-2 text-center">*/}
        {/*        <p className="description" style={{ maxWidth: 800, margin: "0 auto 16px" }}>*/}
        {/*          Kérdésed van? Vedd fel velünk a kapcsolatot – segítünk a bevezetésben és a testreszabásban.*/}
        {/*        </p>*/}
        {/*        <div className="btn-area" style={{ display: "flex", gap: 16, justifyContent: 'center', flexWrap: "wrap", marginTop: 16 }}>*/}
        {/*          <button className="edu-btn" onClick={(e) => { e.preventDefault(); if (window.openContactModal) window.openContactModal(); }}>*/}
        {/*            Kapcsolatfelvétel <i className="icon-arrow-right-line-right"></i>*/}
        {/*          </button>*/}
        {/*        </div>*/}
        {/*      </div>*/}
        {/*    </div>*/}
        {/*  </div>*/}
        {/*</div>*/}

        <IntegrationsCarousel/>
        <FooterOne />
      </Layout>
    </>
  );
};

export default CrmPageContent;
